import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { getThemeDetails } from "@/services/api";
import { useToast } from "@/hooks/useToast";
import { Theme } from "@/types/themes";

interface ThemeDetailsProps {
  themeId: string;
  name: string;
  theme?: Theme;
  onContinue?: () => void;
  onBack?: () => void;
}

export function ThemeDetails({ themeId, name, theme, onContinue, onBack }: ThemeDetailsProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [content, setContent] = useState<{ tagline?: string; description?: string } | null>(null);

  useEffect(() => {
    if (theme?.tagline && theme?.description) {
      setContent({
        tagline: theme.tagline,
        description: theme.description
      });
      return;
    }

    const fetchDetails = async () => {
      setIsLoading(true);
      try {
        const response = await getThemeDetails(themeId);
        if (response.success && response.data) {
          setContent(response.data);
        }
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load theme details"
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, [themeId, theme, toast]);

  return (
    <Card className="w-full animate-slideUp border-border bg-card shadow-lg flex flex-col h-[calc(100dvh-10rem)]">
      <CardContent className="p-6 flex flex-col h-full">
        {/* Main content wrapper */}
        <div className="flex-1 min-h-0 flex flex-col">
          {/* Scrollable content */}
          <div className="overflow-y-auto flex-1">
            {/* Header */}
            <div className="text-center">
              <h2 className="text-2xl font-bold text-foreground mb-2">
                {name}
              </h2>
              {isLoading ? (
                <Skeleton className="h-8 w-3/4 mx-auto" />
              ) : (
                <p className="text-md text-muted-foreground italic font-medium">
                  ~ {content?.tagline || "A new gaming adventure awaits..."}
                </p>
              )}
            </div>

            {/* Content */}
            <div className="space-y-8 mt-8">
              {isLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              ) : (
                <div className="prose prose-invert max-w-none">
                  <p className="text-md text-foreground leading-5">
                    {content?.description || "Embark on an exciting journey in this unique card game experience..."}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Fixed Actions */}
          <div className="flex justify-between items-center pt-6 mt-8 border-t border-border">
            <Button
              variant="ghost"
              onClick={onBack}
              className="text-muted-foreground"
            >
              Back
            </Button>
            <Button
              onClick={onContinue}
              disabled={isLoading}
              className="text-foreground bg-game-accent hover:bg-game-accent/90"
            >
              Continue
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
} 